import { CatalogManager, openCatalogDirectory } from "@/lib/catalog"

import {
	defaultCullingSettings,
	PhotoRecord,
	PhotoFlagStatus,
	ProjectInfoRecord,
	Rating,
	StyleInfoRecord,
	Visibility,
	StyleReferenceRecord,
} from "@/lib/catalog/types"

import { FilesInfo, hasPermission } from "@/storage/file-system"

import {
	hasShownOnboarding,
	isMetadataFinishedAtom,
	loadingStateAtom,
	sessionIdAtom,
} from "../app-state"
import { popDialog, pushDialog, replaceDialog, resetDialogs } from "../dialogs-state"
import { filesAtom } from "../files-state"
import { stateAtom } from "../undoable-state"
import {
	filePathsAtom,
	updateCalibrationPhotos,
	metadataAtom,
	photosAtom,
	anchorPhotosAtom,
	allPhotosAtom,
	cullingInfoAtom,
} from "../photo/photos-state"
import { setImageInfoRecords } from "../processing/image-info"
import { store } from "../store"
import { startCalibration } from "../style/calibration"
import {
	currentStyleAtom,
	isLocalStyle,
	isRemoteStyle,
	ProjectSetupStyle,
	saveAsCurrentStyle,
	showPublishCurrentStyleDialog,
	styleReferencesAtom,
} from "../style/style-state"
import { projectClosingAtom } from "../ui-views-state"
import { finalizeSession, navigateToProjectRoute } from "@/routing/project-session"
import { routes } from "@/utils/routes"
import {
	fetchOrCreateProject,
	getCompatibleFiles,
	setupProjectUIState,
	startEditing,
} from "./project"
import { ProjectItem } from "./projects-service"
import {
	isManualModeAtom,
	normalizeFilterArray,
	projectAtom,
	projectPathsAtom,
	updateCurrentProject,
} from "./projects-state"
import { ImportLoadingDialog, LoadingDialog } from "@/components/Dialogs/LoadingDialog"
import { CullingOnboardingDialog } from "@/components/Dialogs/CullingOnboardingDialog"
import { PresetService } from "@/store/presets/presets-service"
import { analytics } from "@/telemetry/amplitude"
import { Sentry } from "@/telemetry/sentry"
import { PhotoTooLargeDialog } from "@/components/Dialogs/PhotoTooLargeDialog"
import { handleProjectError } from "./handle-project-error"
import { PreviewSyncService } from "../previews/previews-sync"
import { resumeTasksOrClearStatus } from "../processing/image-processing"
import {
	applyStyleToAllPhotosWithPreClustering,
	cleanupPendingStyleApplication,
} from "../style/apply-style"
import { isProjectLocked } from "./lock-project"
import { metadataImporter } from "../importing/metadata-importing"
import { cullingImporter } from "../importing/culling-importing"
import { previewImporter } from "../importing/preview-importing"
import { getCurrentProjectMetadataAnalyticsInfo } from "./project-metadata"
import { AI } from "@/lib/ai/ai"
import { ColorMatchImageService } from "../colorMatch/color-match-service"
import {
	fetchAndRegisterStyleLuts,
	publishStyleDraftWithRecovery,
	useAndStoreRemoteStyleInProject,
} from "@/lib/catalog/helpers"
import { ColorTag } from "@/utils/exif"
import { createStyleDraftFromLegacyStyle } from "../style/create-style"
import { awaitMaybePromise } from "@/utils/promise"
import toast from "react-hot-toast"
import { loadCurrentFile } from "../editor/photo-loading"
import { StyleService } from "../style/style-service"
import { AxiosError } from "axios"
import { StyleDeletedDialog, StyleSyncDialog } from "@/components/Dialogs/StyleSyncDialog"
import { StyleRecord } from "@/storage/indexeddb"
import { editClusteringImporter } from "../importing/edit-clustering-importing"
import { isNetworkConnectivityError, showActiveNetworkErrorToast } from "@/api/network-behavior"
import { popActiveLoadingDialogs } from "@/store/loading-dialog-utils"
import { waitForCullingMetadata } from "./wait-for-culling-metadata"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"

interface ProjectSetupOptions {
	handle: FileSystemDirectoryHandle
	aiStyleTriggerSource?: string | null
	selectedStyle?: ProjectSetupStyle | "new" | "manual" | null
	needsSetup?: boolean
	isExpressSetup?: boolean
	isCulling?: boolean
	useCurrentSessionUI?: boolean
	isSmartCulling?: boolean
	existingProject?: ProjectItem
	existingProjectInfo?: ProjectInfoRecord
	existingProjectStyle?: StyleInfoRecord | StyleRecord //Will migrate style if StyleRecord is passed in
	filesInfo?: FilesInfo
}

export async function startMetadataPrefetching(
	project: ProjectItem,
	handle: FileSystemDirectoryHandle,
	filesInfo?: FilesInfo,
) {
	// Callers must open the catalog for this handle before prefetching because base state
	// setup persists discovered files through CatalogManager.
	const filesResult = await setupProjectBaseState(project, { handle, filesInfo })
	if (!filesResult) {
		return
	}

	// Load persisted state now, but wait for setup confirmation before culling.
	await cullingImporter.start(project)
	previewImporter.start(project)
	void metadataImporter
		.start(project, filesResult.filesInfo, true)
		.catch((error) => Sentry.captureException(error))
}

//Sets up the base state of the project (project, photos, metadata, files)
export async function setupProjectBaseState(project: ProjectItem, options: ProjectSetupOptions) {
	const filesResult = await processFilesForProject(project, options)
	if (!filesResult) {
		resetDialogs()
		cancelOpenProject("No compatible files found")
		return
	}
	// Base-state setup can run before full setUpProject (e.g. prefetch/create flows).
	// Normalize defaults here so filters are initialized before projectAtom is set.
	updateProjectSettings(project)
	//Stores current project
	store.set(projectAtom, project)

	store.set(metadataAtom, filesResult.metadata)

	store.set(photosAtom, filesResult.photos)

	store.set(filesAtom, filesResult.files)

	await updateCurrentProject(project).catch(console.error)

	//sync project cover to BE if needed
	void PreviewSyncService.syncProjectCoverIfNeeded(project).catch((e) =>
		Sentry.captureException(e, {
			tags: {
				source: "setupProjectBaseState:syncProjectCoverIfNeeded",
			},
		}),
	)

	return filesResult
}

export async function setUpProject(options: ProjectSetupOptions) {
	// Get project ID early to manage session state
	const projectId = options.existingProject?.id ?? options.existingProjectInfo?.id

	try {
		// Keep permission checks inside try/finally so early exits still reconcile
		// session state and don't leave the router stuck in "opening".
		if (!(await hasPermission(options.handle, "readwrite"))) {
			cancelOpenProject("missing_project_permission")
			return
		}

		// Wait for any ongoing project close to complete before setting up new project
		while (store.get(projectClosingAtom)) {
			await new Promise((resolve) => setTimeout(resolve, 100))
		}

		// Open catalog before checking lock so the worker has a valid directory
		await openCatalogDirectory(options.handle)

		if (await isProjectLocked()) {
			return
		}

		//Lock project so others cannot access
		// Pass undefined to use this.directory in the worker (avoids DataCloneError in Safari)
		await awaitMaybePromise(CatalogManager.lockProject(undefined, store.get(sessionIdAtom)))

		//Get project info
		const projectInfo = options.existingProjectInfo ?? (await CatalogManager.fetchProjectInfo())
		const project =
			options.existingProject ??
			(await fetchOrCreateProject(options.handle, projectInfo)).project

		//Project settings
		updateProjectSettings(project, projectInfo)

		//Files
		let filesInfo: FilesInfo | undefined = undefined
		//Setup project base state if needed (no prefetching)
		if (metadataImporter.filesInfo && metadataImporter.startProjectId === project.id) {
			filesInfo = metadataImporter.filesInfo
		} else {
			const filesResult = await setupProjectBaseState(project, options)
			if (!filesResult) {
				return
			}
			filesInfo = filesResult.filesInfo
		}

		//Style and mode (needsSetup/culling/manual)
		const styleResult = await processStyleForProject(project, options, projectInfo)
		if (styleResult.shouldAbortSetup) {
			return
		}
		const currentStyle = styleResult.currentStyle

		updateProjectSetupStatus(project, options, projectInfo)
		// Update project in memory immediately to reflect in UI
		store.set(projectAtom, project)
		// Also Update project in BE and catalog
		updateCurrentProject(project).catch(console.error)

		//Prepare AI info
		const imageInfoRecords = await CatalogManager.fetchImageInfoRecords()
		setImageInfoRecords(imageInfoRecords)

		//Restart processing if there are unfinished tasks
		resumeTasksOrClearStatus(options.aiStyleTriggerSource)

		setupProjectUIState(
			project.isCulling ? "culling" : "editing",
			project,
			projectInfo,
			getPreferredUIState(options.useCurrentSessionUI),
		)
		await navigateToProjectRoute(project.id)

		resetDialogs()

		//Culling mode
		if (project.isCulling) {
			// Defense-in-depth: make sure any leftover edit-mode AI pipeline is stopped
			// before starting culling. switchToCulling does this earlier to close the race
			// with processingQueue, but other callers of setUpProject end up here too.
			cleanupPendingStyleApplication()
			await editClusteringImporter.pauseAndCancel({ cleanupModels: false })

			void AI.cleanupModels()
			if (!(await waitForCullingMetadata(project, filesInfo))) return

			await cullingImporter.start(project)
			previewImporter.start(project)
			void cullingImporter.startCulling().catch((error) => Sentry.captureException(error))

			if (!hasShownOnboarding("cullingShown")) {
				pushDialog(CullingOnboardingDialog)
			}
		} else {
			const isCullingFinished =
				Object.keys(store.get(cullingInfoAtom)).length === store.get(allPhotosAtom).length
			await cullingImporter.pauseAndCancel({ cleanupModels: isCullingFinished })

			//Editing mode
			if (store.get(isManualModeAtom)) {
				previewImporter.start(project)
			} else {
				await previewImporter.pauseAndCancel()
			}

			if (project.needsSetup) {
				// Start edit clustering importer in background during setup
				if (!isCullingFinished) {
					const allPhotoIds = store.get(allPhotosAtom).map((p) => p.id)
					void editClusteringImporter.start(project, allPhotoIds, [])
				}

				if (!project.isExpressSetup && !store.get(isMetadataFinishedAtom)) {
					replaceDialog(ImportLoadingDialog)
				} else if (project.isExpressSetup !== undefined) {
					updateCalibrationPhotos(project, project.isExpressSetup)
					startCalibration()
				}
			} else if (currentStyle) {
				void updateCurrentStyleIfNeeded(currentStyle, project, options.handle).then(
					(updatedStyle) => {
						if (updatedStyle) {
							applyStyleToAllPhotosWithPreClustering(
								updatedStyle,
								undefined,
								options.aiStyleTriggerSource,
							)
						}
					},
				)
			}
		}

		void loadCurrentFile()
		if (!project.isCulling) {
			void metadataImporter
				.start(project, filesInfo)
				.then((completed) => {
					if (completed && project.needsSetup && !project.isExpressSetup) {
						resetDialogs()
						updateCalibrationPhotos(project, false)
						startCalibration()
					}
				})
				.catch((error) => Sentry.captureException(error))
		}

		//Other work that can be done in the background
		void PresetService.prefetchPresets()
		void ColorMatchImageService.syncImages()

		showLargePhotosAlertIfNeeded()

		analytics.trackEvent({
			label: "project_opened",
			type: "event",
			customProperties: getCurrentProjectMetadataAnalyticsInfo(),
		})
	} catch (error) {
		if (isNetworkConnectivityError(error)) {
			popActiveLoadingDialogs()
			showActiveNetworkErrorToast()
			return
		}

		//Clear loading
		store.set(loadingStateAtom, { status: null })
		resetDialogs()
		void handleProjectError(error, { source: "setup_project" })
		cancelOpenProject(error instanceof Error ? error.message : "Project setup error")
	} finally {
		finalizeSession(projectId)
	}
}

function getPreferredUIState(useSessionUI?: boolean): {
	preferredPhotosViewState?: "grid" | "focus" | "before/after"
	preferredCurrentPhotoId?: string
} {
	if (!useSessionUI) {
		return {}
	}

	const currentState = store.get(stateAtom)
	return {
		preferredPhotosViewState: currentState.photosView,
		preferredCurrentPhotoId: currentState.currentPhotoId ?? undefined,
	}
}

async function updateCurrentStyleIfNeeded(
	style: StyleInfoRecord,
	project: ProjectItem,
	handle: FileSystemDirectoryHandle,
) {
	// Yuhe note: don't forget to sync the local field prioritizeImageIds back to style after updating
	let updatedStyle = style
	try {
		const remoteStyle = await StyleService.fetchStyle(style.styleId)
		updatedStyle = StyleService.responseToStyleInfo(
			remoteStyle,
			style.prioritizeImageIds,
			style.globalBiases,
		)
		await fetchAndRegisterStyleLuts(updatedStyle.lutUrls)
		if (remoteStyle.version > style.version) {
			//style has major update (references), need to handle with dialog
			const result = await handleStyleUpdated(style, updatedStyle)
			updatedStyle = {
				...result.styleInfo,
				prioritizeImageIds: style.prioritizeImageIds,
				globalBiases: style.globalBiases,
			}
			await saveAsCurrentStyle(result.styleInfo, result.references, result.skipReprocessing)
		} else {
			//Sync style metadata in case it was changed
			await saveAsCurrentStyle(updatedStyle)
		}
		return updatedStyle
	} catch (error) {
		if (error instanceof AxiosError && error.response?.status === 404) {
			const result = await handleStyleDeleted(style, project, handle)
			if (!result) {
				return null
			}
			updatedStyle = result.styleInfo
			await saveAsCurrentStyle(updatedStyle, result.references, true)
		}

		return updatedStyle
	}
}

function handleStyleUpdated(
	localStyle: StyleInfoRecord,
	remoteStyle: StyleInfoRecord,
): Promise<{
	styleInfo: StyleInfoRecord
	references?: StyleReferenceRecord[]
	skipReprocessing?: boolean
}> {
	return new Promise((resolve) => {
		replaceDialog(StyleSyncDialog, {
			updatedStyle: remoteStyle,
			trackingViewName: "handle_updated_style_dialog",
			onOptionSelected: (option) => {
				switch (option) {
					case "publish_current":
						showPublishCurrentStyleDialog(
							localStyle,
							{ disableClose: true, canGoBack: true },
							undefined,
							(result) => {
								resolve({ ...result, skipReprocessing: true })
							},
						)
						break
					case "update": {
						//Handled in StyleSyncDialog
						resolve({ styleInfo: remoteStyle })
						break
					}
				}
			},
		})
	})
}

function handleStyleDeleted(
	localStyle: StyleInfoRecord,
	project: ProjectItem,
	handle: FileSystemDirectoryHandle,
): Promise<{ styleInfo: StyleInfoRecord; references?: StyleReferenceRecord[] } | null> {
	return new Promise((resolve) => {
		replaceDialog(StyleDeletedDialog, {
			trackingViewName: "handle_deleted_style_dialog",
			onOptionSelected: (option) => {
				switch (option) {
					case "publish_current":
						showPublishCurrentStyleDialog(
							localStyle,
							{ disableClose: true, canGoBack: true },
							undefined,
							(result) => {
								resolve(result)
							},
						)
						break
					case "select_new":
						void startEditing({
							handle: handle,
							existingProject: project,
							disableClose: true,
							clearExistingEdits: true,
						})
						resolve(null)
						break
				}
			},
		})
	})
}

function updateProjectSetupStatus(
	project: ProjectItem,
	options: ProjectSetupOptions,
	projectInfo?: ProjectInfoRecord,
) {
	if (options.isExpressSetup !== undefined) {
		//use new setup options if available
		project.isExpressSetup = options.isExpressSetup
	} else if (projectInfo?.isExpressSetup === true || projectInfo?.isExpressSetup === false) {
		//use catalog info as fallback
		project.isExpressSetup = projectInfo.isExpressSetup
	} else {
		//Legacy catalog
		project.isExpressSetup = store.get(anchorPhotosAtom).length === 0
	}

	if (project.needsSetup && options.isExpressSetup !== undefined) {
		project.displayGroup = project.isExpressSetup ? "all" : "suggested"
	}
}

function updateProjectSettings(project: ProjectItem, projectInfo?: ProjectInfoRecord) {
	project.styleId = projectInfo?.styleId ?? project.styleId ?? null
	project.needsSetup = projectInfo?.needsSetup ?? project.needsSetup
	project.isManual = projectInfo?.isManual ?? project.isManual
	project.isCulling = projectInfo?.isCulling ?? project.isCulling

	project.sortOption = projectInfo?.sortOption ?? project.sortOption ?? "filenameAscending"
	project.selectedTags = projectInfo?.selectedTags ?? project.selectedTags
	project.suggestedKeeperCount = projectInfo?.suggestedKeeperCount ?? project.suggestedKeeperCount

	project.ratingFilters =
		normalizeFilterArray<Rating>(projectInfo?.ratingFilters ?? project.ratingFilters) ?? []
	project.pathFilters =
		normalizeFilterArray<string>(projectInfo?.pathFilters ?? project.pathFilters) ?? [] //need to get all paths
	project.colorFilters =
		normalizeFilterArray<ColorTag>(projectInfo?.colorFilters ?? project.colorFilters) ?? []
	project.cameraFilters =
		normalizeFilterArray<string>(projectInfo?.cameraFilters ?? project.cameraFilters) ?? []
	project.flagFilters =
		normalizeFilterArray<PhotoFlagStatus>(projectInfo?.flagFilters ?? project.flagFilters) ?? []
	project.fileFormatFilters =
		normalizeFilterArray<string>(projectInfo?.fileFormatFilters ?? project.fileFormatFilters) ??
		[]
	project.visibilityFilters = normalizeFilterArray<Visibility>(
		projectInfo?.visibilityFilters ?? project.visibilityFilters,
	) ?? ["visible"]
	project.displayGroup = projectInfo?.displayGroup ?? project.displayGroup ?? "all"
	project.publishCollectionId = projectInfo?.publishCollectionId ?? project.publishCollectionId
	project.publishGalleryId = projectInfo?.publishGalleryId ?? project.publishGalleryId

	if (!project.isSampleProject || project.cullingSettings === undefined) {
		project.cullingSettings =
			projectInfo?.cullingSettings ?? project.cullingSettings ?? defaultCullingSettings
	}
}

async function processStyleForProject(
	project: ProjectItem,
	options: ProjectSetupOptions,
	projectInfo?: ProjectInfoRecord,
): Promise<{
	currentStyle: StyleInfoRecord | null
	shouldAbortSetup: boolean
}> {
	//Migration for old StyleRecord to StyleInfoRecord if needed
	if (options.existingProjectStyle && !isLocalStyle(options.existingProjectStyle)) {
		pushDialog(LoadingDialog, { title: i18n.t("store:setupProject.migratingProjectStyle") })
		try {
			const migratedStyleDraft = createStyleDraftFromLegacyStyle(options.existingProjectStyle)
			const publishResult = await publishStyleDraftWithRecovery(migratedStyleDraft, {
				projectStyleType: "My AI Style",
				onDuplicateNameResolved: async (result) => {
					await saveAsCurrentStyle(result.styleInfo, result.references, true)
					await awaitMaybePromise(CatalogManager.storeStyleInfo(result.styleInfo))
					await awaitMaybePromise(
						CatalogManager.replaceStyleReferences(result.references),
					)
					await setUpProject({
						...options,
						selectedStyle: result.styleInfo,
						existingProject: project,
						existingProjectInfo: projectInfo,
						existingProjectStyle: undefined,
					})
				},
				trackingViewName: "style_naming_dialog",
			})
			if (!publishResult) {
				return {
					currentStyle: null,
					shouldAbortSetup: true,
				}
			}
			await saveAsCurrentStyle(publishResult.styleInfo, publishResult.references, true)
			await awaitMaybePromise(CatalogManager.storeStyleInfo(publishResult.styleInfo))
			await awaitMaybePromise(CatalogManager.replaceStyleReferences(publishResult.references))
			//Temporarily keep old style around in case migration fails and user need manual support
			// await CatalogManager.removeProjectStyle(options.handle)
			options.existingProjectStyle = publishResult.styleInfo
			popDialog()
		} catch (error) {
			console.error("Error migrating project style:", error)
			toast.error(i18n.t("components:setupProject.failedToMigrateProjectStyle"))
			resetDialogs()
			return {
				currentStyle: null,
				shouldAbortSetup: true,
			}
		}
	}

	//Style
	let currentStyle: StyleInfoRecord | null = null
	if (options.isCulling || (options.isCulling === undefined && projectInfo?.isCulling)) {
		project.isCulling = true
		project.needsSetup = false
		// Keep existing styleId on the project even in culling mode so edit-mode re-entry can
		// restore the previous style without prompting selection again.
		if (options.existingProjectStyle && "styleId" in options.existingProjectStyle) {
			project.styleId = options.existingProjectStyle.styleId
		} else if (projectInfo?.styleId) {
			project.styleId = projectInfo.styleId
		}
	} else if (
		options.selectedStyle === "manual" ||
		(!options.selectedStyle && !!projectInfo?.isManual)
	) {
		currentStyle = null
		project.styleId = null
		project.needsSetup = false
		project.isManual = true
		project.isCulling = false
	} else if (options.selectedStyle === "new") {
		currentStyle = null
		project.styleId = null
		project.needsSetup = true
		project.isManual = false
		project.isCulling = false
	} else if (options.selectedStyle) {
		if (isRemoteStyle(options.selectedStyle)) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			currentStyle = await useAndStoreRemoteStyleInProject(options.selectedStyle)
		} else {
			currentStyle = options.selectedStyle
		}

		project.styleId = currentStyle.styleId
		project.needsSetup = options.needsSetup
		project.isManual = false
		project.isCulling = false
	} else if (options.existingProjectStyle && "styleId" in options.existingProjectStyle) {
		//Use existing project style
		currentStyle = options.existingProjectStyle
		project.styleId = currentStyle.styleId
		project.needsSetup = options.needsSetup
		project.isManual = false
		project.isCulling = false
	}
	store.set(currentStyleAtom, currentStyle)
	if (currentStyle?.styleId) {
		const references = await CatalogManager.fetchStyleReferences(currentStyle.styleId)
		store.set(styleReferencesAtom, references)
	}

	return {
		currentStyle,
		shouldAbortSetup: false,
	}
}

async function processFilesForProject(project: ProjectItem, options: ProjectSetupOptions) {
	const filesInfo = options.filesInfo ?? (await getCompatibleFiles(options.handle))
	if (!filesInfo) {
		return
	}

	store.set(projectPathsAtom, filesInfo.paths)
	store.set(filePathsAtom, filesInfo.filePaths)

	project.photoCount = filesInfo.files.length
	project.pathFilters = filesInfo.paths.map((p) => p.path)

	return {
		filesInfo,
		...(await CatalogManager.storeOrUpdatePhotosFromFiles(filesInfo.files, filesInfo.fileIds)),
	}
}

function showLargePhotosAlertIfNeeded() {
	const latestMetadata = store.get(metadataAtom)
	const photos = store.get(photosAtom)
	const photosLargerThan100Mps: PhotoRecord[] = []

	for (const photoId in photos) {
		const photo = photos[photoId]
		const metadata = latestMetadata[photoId]
		if (
			photo &&
			metadata &&
			metadata.width &&
			metadata.height &&
			metadata.width * metadata.height >= 100e6
		) {
			photosLargerThan100Mps.push(photo)
		}
	}

	if (photosLargerThan100Mps.length > 0) {
		replaceDialog(PhotoTooLargeDialog, {
			photoRecords: photosLargerThan100Mps,
			primary: {
				title: i18n.t("common:action.confirm"),
			},
			trackingViewName: "photos_larger_than_100mp_dialog",
		})
	}
}

export function cancelOpenProject(reason?: string) {
	analytics.trackEvent({ label: "cancel_open_project", type: "event", value: reason })
	store.set(projectAtom, null) //This will clear any project related states set
	void CatalogManager.unlockProject()
	void import("@/router")
		.then(({ router }) => routes.root.navigate(router))
		.catch((error) => {
			console.error("Failed to navigate to root while cancelling project open", error)
		})
		.finally(() => {
			// Keep session in opening state until route leaves /projects/:id to avoid immediate reopen.
			finalizeSession()
		})
}
